import Link from 'next/link';
import Image from 'next/image';
import { EnvelopeIcon, PhoneIcon, MapPinIcon, ClockIcon } from '@heroicons/react/24/outline';

export default function Footer() {
  const industries = [
    { name: 'Pharmaceutical', slug: 'pharmaceutical' },
    { name: 'Retail & Restaurant Chains', slug: 'retail' },
    { name: 'Financial Services', slug: 'financial-services' },
    { name: 'Logistics & Supply Chain', slug: 'logistics' },
    { name: 'Manufacturing', slug: 'manufacturing' },
    { name: 'Healthcare', slug: 'healthcare' },
    { name: 'Oil & Gas', slug: 'oil-gas' },
    { name: 'Energy & Utilities', slug: 'energy-utilities' },
    { name: 'Telecommunications', slug: 'telecommunications' },
    { name: 'CPG / FMCG', slug: 'cpg-fmcg' },
    { name: 'Hospitality & Travel', slug: 'hospitality-travel' },
    { name: 'Insurance', slug: 'insurance' },
    { name: 'Transportation', slug: 'transportation' },
    { name: 'Education', slug: 'education' },
    { name: 'Market Research', slug: 'market-research' },
    { name: 'Technology', slug: 'technology' },
  ];

  return (
    <footer className="bg-[#1B2845] text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-10 sm:py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 md:gap-12">
          {/* Company Section */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <Image
                src="/assets/images/All-SIMAX-LOGOS_-_9_-_Edited-1770650153046.png"
                alt="Simax Systems Logo"
                width={220}
                height={220}
                className="object-contain mb-4"
              />
              <p className="text-sm text-[#C5A572] font-medium mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                Enterprise Data Warehousing Solutions
              </p>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              Transforming business intelligence through unified data architecture
            </p>
            {/* Social Media */}
            <div className="flex items-center gap-4">
              <a
                href="linkedin.com/company/simaxsystems/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-[#C5A572] transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a
                href="https://twitter.com/simaxsystems"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-[#C5A572] transition-colors duration-300"
                aria-label="Twitter"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Quick Links
            </h3>
            <ul className="space-y-3" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li>
                <Link href="/homepage" className="text-sm text-gray-300 hover:text-[#C5A572] transition-colors duration-300">
                  Home
                </Link>
              </li>
              <li>
                <Link href="/solutions" className="text-sm text-gray-300 hover:text-[#C5A572] transition-colors duration-300">
                  Solutions
                </Link>
              </li>
              <li>
                <Link href="/engagement-models" className="text-sm text-gray-300 hover:text-[#C5A572] transition-colors duration-300">
                  Engagement Models
                </Link>
              </li>
              <li>
                <Link href="/our-approach" className="text-sm text-gray-300 hover:text-[#C5A572] transition-colors duration-300">
                  Our Approach
                </Link>
              </li>
              <li>
                <Link href="/blog" className="text-sm text-gray-300 hover:text-[#C5A572] transition-colors duration-300">
                  Blog
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-sm text-gray-300 hover:text-[#C5A572] transition-colors duration-300">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Industries (Condensed) */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Industries
            </h3>
            <ul className="space-y-3" style={{ fontFamily: 'Inter, sans-serif' }}>
              {industries?.slice(0, 6)?.map((industry) => (
                <li key={industry.slug}>
                  <Link
                    href={`/industries/${industry.slug}`}
                    className="text-sm text-gray-300 hover:text-[#C5A572] transition-colors duration-300"
                  >
                    {industry.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link href="/industries" className="text-sm text-[#C5A572] hover:text-white transition-colors duration-300 font-medium">
                  View All Industries →
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Contact Us
            </h3>
            <ul className="space-y-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li className="flex items-start gap-3">
                <EnvelopeIcon className="w-5 h-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                <a href="mailto:contact@simaxsystems.com" className="text-sm text-gray-300 hover:text-[#C5A572] transition-colors duration-300">
                  contact@simaxsystems.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <PhoneIcon className="w-5 h-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <a href="tel:+917893066401" className="text-sm text-gray-300 hover:text-[#C5A572] transition-colors duration-300">
                   +91-78930 66401
                  </a>
                  <a href="tel:+917702128723" className="text-sm text-gray-300 hover:text-[#C5A572] transition-colors duration-300">
                    +91 77021 28723
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPinIcon className="w-5 h-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300">
                  9th Floor, RMZ Latitude, Hebbal Kempapura<br />
                 Bengaluru, Karnataka, India - 560024
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ClockIcon className="w-5 h-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300">
                  Mon - Fri: 9:00 AM - 6:00 PM PST
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-400" style={{ fontFamily: 'Inter, sans-serif' }}>
              © 2026 Simax Systems. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              <Link href="/privacy-policy" className="text-gray-400 hover:text-[#C5A572] transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link href="/terms-of-service" className="text-gray-400 hover:text-[#C5A572] transition-colors duration-300">
                Terms of Service
              </Link>
              <Link href="/cookie-policy" className="text-gray-400 hover:text-[#C5A572] transition-colors duration-300">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}